import { useState } from "react";
import { Mail, CheckCircle2, AlertCircle } from "lucide-react";
import { sendContact } from "@/lib/api";
import { CONTACT } from "@/constants/testIds";

const LIMITS = {
  name: { min: 2, max: 100, label: "Nom" },
  subject: { min: 2, max: 200, label: "Sujet" },
  message: { min: 5, max: 5000, label: "Message" },
};

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function validateForm(form) {
  const errors = [];
  if (form.name.trim().length < LIMITS.name.min) {
    errors.push(`Votre nom doit contenir au moins ${LIMITS.name.min} caractères.`);
  }
  if (!EMAIL_RE.test(form.email.trim())) {
    errors.push("Veuillez saisir un email valide.");
  }
  if (form.subject.trim().length < LIMITS.subject.min) {
    errors.push(`Le sujet doit contenir au moins ${LIMITS.subject.min} caractères.`);
  }
  if (form.message.trim().length < LIMITS.message.min) {
    errors.push(`Votre message doit contenir au moins ${LIMITS.message.min} caractères.`);
  }
  return errors;
}

/** Convert any backend / network error into a readable French message. */
function formatApiError(err) {
  const status = err?.response?.status;
  const detail = err?.response?.data?.detail;

  // Pydantic 422 — detail is an array of {loc, msg, type, ctx}
  if (Array.isArray(detail)) {
    const fieldLabels = { name: "Nom", email: "Email", subject: "Sujet", message: "Message" };
    return detail
      .map((d) => {
        const field = d.loc?.[d.loc.length - 1];
        const label = fieldLabels[field] || field || "Champ";
        if (d.type === "string_too_short") {
          return `${label} : ${d.ctx?.min_length || 0} caractères minimum.`;
        }
        if (d.type === "string_too_long") {
          return `${label} : ${d.ctx?.max_length || 0} caractères maximum.`;
        }
        if (d.type === "value_error" || d.type?.includes("email")) {
          return `${label} : format invalide.`;
        }
        return `${label} : ${d.msg || "valeur invalide"}.`;
      })
      .join(" ");
  }

  if (typeof detail === "string") return detail;
  if (status === 429) return "Trop de tentatives, merci de réessayer dans un instant.";
  if (status >= 500) return "Service temporairement indisponible. Réessayez dans une minute.";
  if (err?.code === "ERR_NETWORK") return "Connexion impossible. Vérifiez votre réseau et réessayez.";
  return "Une erreur est survenue. Réessayez ou contactez-nous via opmp@monprecieux.com.";
}

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
  const [status, setStatus] = useState(null); // 'success' | 'error' | null
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const update = (field) => (e) => setForm((f) => ({ ...f, [field]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    const clientErrors = validateForm(form);
    if (clientErrors.length > 0) {
      setStatus("error");
      setError(clientErrors.join(" "));
      return;
    }

    setSubmitting(true);
    try {
      await sendContact({
        name: form.name.trim(),
        email: form.email.trim(),
        subject: form.subject.trim(),
        message: form.message.trim(),
      });
      setStatus("success");
      setForm({ name: "", email: "", subject: "", message: "" });
    } catch (err) {
      setStatus("error");
      setError(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  const messageCount = form.message.length;
  const messageCountColor =
    messageCount === 0
      ? "text-[hsl(var(--text-dim))]"
      : messageCount < LIMITS.message.min
      ? "text-amber-400"
      : "text-[hsl(var(--text-muted))]";

  return (
    <div className="container-mp pt-12 pb-16">
      <div className="max-w-2xl mx-auto">
        <div className="text-xs uppercase tracking-[0.25em] text-gold-bright mb-2">
          ✦ Nous contacter
        </div>
        <h1 className="font-display text-3xl md:text-4xl mb-3">
          Une question&nbsp;? Écrivez-nous.
        </h1>
        <p className="text-[hsl(var(--text-muted))] mb-10">
          Notre équipe vous répond rapidement, en moins de 24 heures ouvrées.
        </p>

        {status === "success" && (
          <div
            data-testid={CONTACT.success}
            className="mb-6 p-4 rounded-xl border border-[hsl(var(--gold))]/40 bg-[hsl(var(--gold))]/[0.08] flex items-start gap-3"
          >
            <CheckCircle2 size={18} className="text-gold-bright mt-0.5 shrink-0" />
            <div className="text-sm">
              <div className="font-medium text-gold-bright">Message envoyé.</div>
              <div className="text-[hsl(var(--text-muted))] mt-1">
                Merci, nous reviendrons vers vous très vite. Un email de confirmation vient de partir.
              </div>
            </div>
          </div>
        )}

        {status === "error" && error && (
          <div
            data-testid="contact-error"
            className="mb-6 p-4 rounded-xl border border-red-500/30 bg-red-500/[0.08] flex items-start gap-3"
          >
            <AlertCircle size={18} className="text-red-300 mt-0.5 shrink-0" />
            <div className="text-sm text-red-100">{error}</div>
          </div>
        )}

        <form onSubmit={onSubmit} noValidate className="grid gap-4">
          <div>
            <label className="text-xs uppercase tracking-[0.2em] text-[hsl(var(--text-dim))] block mb-2">
              Votre nom
            </label>
            <input
              type="text"
              required
              maxLength={LIMITS.name.max}
              value={form.name}
              onChange={update("name")}
              data-testid={CONTACT.name}
              className="field"
              placeholder="Frodon Sacquet"
            />
          </div>
          <div>
            <label className="text-xs uppercase tracking-[0.2em] text-[hsl(var(--text-dim))] block mb-2">
              Email
            </label>
            <input
              type="email"
              required
              value={form.email}
              onChange={update("email")}
              data-testid={CONTACT.email}
              className="field"
              placeholder="vous@exemple.com"
            />
          </div>
          <div>
            <label className="text-xs uppercase tracking-[0.2em] text-[hsl(var(--text-dim))] block mb-2">
              Sujet
            </label>
            <input
              type="text"
              required
              maxLength={LIMITS.subject.max}
              value={form.subject}
              onChange={update("subject")}
              data-testid={CONTACT.subject}
              className="field"
              placeholder="Question sur une reprise"
            />
          </div>
          <div>
            <div className="flex items-baseline justify-between mb-2">
              <label className="text-xs uppercase tracking-[0.2em] text-[hsl(var(--text-dim))]">
                Message
              </label>
              <span className={`text-[10px] tabular-nums ${messageCountColor}`}>
                {messageCount} / {LIMITS.message.max} (min. {LIMITS.message.min})
              </span>
            </div>
            <textarea
              required
              maxLength={LIMITS.message.max}
              rows={6}
              value={form.message}
              onChange={update("message")}
              data-testid={CONTACT.message}
              className="field resize-y"
              placeholder="Bonjour, je souhaiterais…"
            />
          </div>
          <div className="flex items-center justify-between gap-3 mt-2">
            <div className="text-xs text-[hsl(var(--text-dim))] flex items-center gap-2">
              <Mail size={14} /> Nous respectons votre vie privée.
            </div>
            <button
              type="submit"
              disabled={submitting}
              data-testid={CONTACT.submit}
              className="btn-gold disabled:opacity-50"
            >
              {submitting ? "Envoi…" : "Envoyer le message →"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
